// Document currency — mirrors backend/currency.py.
//
// Every quotation / order / invoice carries a `currency` code ("INR" or
// "USD"). Records saved before the field existed have none and are INR, so
// `docCurrency` returns INR for them. Amounts are always shown in the
// document's own currency — nothing here converts between currencies.

export const DEFAULT_CURRENCY = "INR";

export const CURRENCIES = [
  // INR keeps the viewer's default number grouping (exactly as before);
  // USD always uses thousands grouping.
  { code: "INR", symbol: "₹", label: "Indian Rupee (INR ₹)", locale: undefined },
  { code: "USD", symbol: "$", label: "US Dollar (USD $)", locale: "en-US" },
];

const BY_CODE = Object.fromEntries(CURRENCIES.map((c) => [c.code, c]));

export function docCurrency(doc) {
  const code = String(doc?.currency || "").toUpperCase();
  return BY_CODE[code] ? code : DEFAULT_CURRENCY;
}

export function currencySymbol(code) {
  return (BY_CODE[String(code || "").toUpperCase()] || BY_CODE[DEFAULT_CURRENCY]).symbol;
}

// Digit grouping follows the currency's locale (see CURRENCIES).
export function formatAmount(value, opts = {}) {
  const { decimals, maximumFractionDigits = 2, minimumFractionDigits, currency = DEFAULT_CURRENCY } = opts;
  const n = Number(value || 0);
  const locale = (BY_CODE[String(currency).toUpperCase()] || BY_CODE[DEFAULT_CURRENCY]).locale;
  return n.toLocaleString(locale, {
    minimumFractionDigits: decimals ?? minimumFractionDigits ?? 0,
    maximumFractionDigits: decimals ?? maximumFractionDigits,
  });
}

// "₹ 1,000.00" / "$ 100.00" — or with `withCode`, "₹ 1,000.00 INR".
export function formatMoney(value, currency = DEFAULT_CURRENCY, opts = {}) {
  const code = BY_CODE[String(currency || "").toUpperCase()] ? String(currency).toUpperCase() : DEFAULT_CURRENCY;
  const s = `${currencySymbol(code)} ${formatAmount(value, { ...opts, currency: code })}`;
  return opts.withCode ? `${s} ${code}` : s;
}

// Sum `field` per document currency: { INR: 1200, USD: 30 }. Amounts in
// different currencies are never added together.
export function totalsByCurrency(docs, get = (d) => d?.grand_total) {
  const out = {};
  for (const d of docs || []) {
    const c = docCurrency(d);
    out[c] = Math.round(((out[c] || 0) + Number(get(d) || 0)) * 100) / 100;
  }
  return out;
}

// "₹ 1,200 + $ 30" — INR first; "₹ 0" when empty.
export function formatCurrencyTotals(totals, opts = {}) {
  const codes = CURRENCIES.map((c) => c.code).filter((c) => totals && totals[c] != null);
  if (!codes.length) return formatMoney(0, DEFAULT_CURRENCY, opts);
  return codes.map((c) => formatMoney(totals[c], c, opts)).join(" + ");
}
